/**
 * Code-box input logic — the S1 code-drop grammar (FIRST NIGHT S1 / MATCH
 * POINT §7): N large boxes, auto-advance, paste-detect, backspace walks left.
 * Pure state functions so the interaction logic is unit-testable without DOM;
 * the CodeBoxes component is a thin render over these.
 *
 * Two charsets: "alnum" (night codes — BLG- prefix rendered, not typed) and
 * "digits" (the 6-digit OTP, autocomplete="one-time-code").
 */

export type CodeCharset = "alnum" | "digits";

export interface CodeBoxState {
  /** One slot per box; "" = empty. */
  values: string[];
  /** The box that should hold focus. */
  focusIndex: number;
  charset: CodeCharset;
}

function clean(text: string, charset: CodeCharset): string {
  const upper = text.toUpperCase();
  // Night codes arrive as "BLG-XXXX" — the prefix is rendered, never typed.
  const unprefixed = charset === "alnum" ? upper.replace(/^BLG-/, "") : upper;
  const allowed = charset === "digits" ? /[^0-9]/g : /[^0-9A-Z]/g;
  return unprefixed.replace(allowed, "");
}

export function emptyCode(length: number, charset: CodeCharset): CodeBoxState {
  return { values: Array.from({ length }, () => ""), focusIndex: 0, charset };
}

/** Type one character into the focused box; focus auto-advances. */
export function inputChar(state: CodeBoxState, raw: string): CodeBoxState {
  const char = clean(raw, state.charset).slice(0, 1);
  if (char === "") return state;
  const values = [...state.values];
  values[state.focusIndex] = char;
  const focusIndex = Math.min(state.focusIndex + 1, values.length - 1);
  return { ...state, values, focusIndex };
}

/**
 * Backspace: clear the focused box if it holds a value, otherwise step left
 * and clear that one (the familiar OTP-box walk).
 */
export function backspace(state: CodeBoxState): CodeBoxState {
  const values = [...state.values];
  if (values[state.focusIndex] !== "") {
    values[state.focusIndex] = "";
    return { ...state, values };
  }
  const focusIndex = Math.max(state.focusIndex - 1, 0);
  values[focusIndex] = "";
  return { ...state, values, focusIndex };
}

/**
 * Paste-detect: strip the rendered prefix + noise, fill from box 0, focus the
 * first empty box (or the last box when the paste fills the code).
 */
export function paste(state: CodeBoxState, text: string): CodeBoxState {
  const chars = clean(text, state.charset).slice(0, state.values.length).split("");
  if (chars.length === 0) return state;
  const values = state.values.map((_, i) => chars[i] ?? "");
  const firstEmpty = values.findIndex((v) => v === "");
  const focusIndex = firstEmpty === -1 ? values.length - 1 : firstEmpty;
  return { ...state, values, focusIndex };
}

export function isComplete(state: CodeBoxState): boolean {
  return state.values.every((v) => v !== "");
}

export function codeValue(state: CodeBoxState): string {
  return state.values.join("");
}
